import type { SalesTaxLineV2 } from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import { formatCurrency } from "../../../../lib/money";
import type { AuditTableColumn } from "../../../audit/AuditTable";
import SummaryTableModal from "../../components/SummaryTableModal";
import { EMPTY_VALUE, formatOptional } from "./financialFormat";

interface SalesTaxModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lines: SalesTaxLineV2[];
  total?: number;
  /** The deal's total selling price, shown in place of the vehicle selling price row's amount. */
  totalSellingPrice?: number;
}

const VEHICLE_SELLING_PRICE_ROW = "VEHICLE SELLING PRICE";

// Read-only per-item sales tax breakdown, opened from the "Sales Tax" link
function SalesTaxModal({ open, onOpenChange, lines, total, totalSellingPrice }: SalesTaxModalProperties) {
  const { t } = useTranslation();

  // Legacy shows the vehicle selling price row as the deal's TOTAL SELLING PRICE.
  const rows = lines.map((line) =>
    line.name?.trim().toUpperCase() === VEHICLE_SELLING_PRICE_ROW
      ? {
          ...line,
          name: t("financial.salesTaxModal.totalSellingPriceItem"),
          taxableAmount: totalSellingPrice ?? line.taxableAmount,
        }
      : line
  );

  const columns: Array<AuditTableColumn<SalesTaxLineV2>> = [
    { key: "name", header: t("financial.modalColumns.taxableItem"), render: (line) => line.name || EMPTY_VALUE },
    {
      key: "taxableAmount",
      header: t("financial.modalColumns.amount"),
      render: (line) => formatOptional(line.taxableAmount, formatCurrency),
    },
    {
      key: "taxMethod",
      header: t("financial.modalColumns.taxMethod"),
      render: (line) => line.taxMethod || EMPTY_VALUE,
    },
    {
      key: "taxAmount",
      header: t("financial.modalColumns.taxAmount"),
      render: (line) => formatOptional(line.taxAmount, formatCurrency),
    },
  ];

  return (
    <SummaryTableModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("financial.salesTaxModal.title")}
      description={t("financial.salesTaxModal.description")}
      caption={t("financial.salesTaxModal.caption")}
      summaryLines={[{ label: t("financial.salesTaxModal.total"), value: formatOptional(total, formatCurrency) }]}
      columns={columns}
      rows={rows}
    />
  );
}

export default SalesTaxModal;
